import { useJourneyStore } from '@/stores/journeyStore'
import { cn } from '@/lib/utils'

type RecommendationDomain = 'market' | 'solution' | 'business'
type RecommendationType = 'critical' | 'warning' | 'info'

interface Recommendation {
  type: RecommendationType
  message: string
  domain?: RecommendationDomain
}

const PRIORITY_DOT: Record<RecommendationType, string> = {
  critical: 'bg-[var(--color-danger)]',
  warning: 'bg-[var(--color-warning)]',
  info: 'bg-text-muted',
}

const DOMAIN_COLOR: Record<RecommendationDomain, string> = {
  market: 'var(--color-market)',
  solution: 'var(--color-solution)',
  business: 'var(--color-business)',
}

const DOMAIN_LABEL: Record<RecommendationDomain, string> = {
  market: 'Market',
  solution: 'Solution',
  business: 'Business',
}

function deriveRecommendations(
  overallProgress: number,
  currentPhaseId: string,
  completedActivities: string[],
  phaseProgress: Record<string, number>,
): Recommendation[] {
  const items: Recommendation[] = []

  if (overallProgress === 0 && completedActivities.length === 0) {
    items.push({
      type: 'info',
      message: 'Start by uploading documents or running interviews to build your evidence base',
      domain: 'market',
    })
  }

  if (overallProgress > 0 && overallProgress < 20) {
    items.push({
      type: 'critical',
      message: 'Product-market fit score is critically low; focus on validating core assumptions',
      domain: 'market',
    })
  }

  const currentProgress = phaseProgress[currentPhaseId] ?? 0
  if (currentPhaseId && currentProgress > 0 && currentProgress < 50) {
    items.push({
      type: 'warning',
      message: 'Current phase needs more progress before advancing',
    })
  }

  if (currentPhaseId && currentProgress >= 90) {
    items.push({
      type: 'info',
      message: 'Ready to advance to the next phase',
    })
  }

  const marketProgress = Object.entries(phaseProgress)
    .filter(([k]) => k.includes('customer') || k.includes('problem'))
    .reduce((sum, [, v]) => sum + v, 0)

  if (marketProgress === 0 && overallProgress > 0) {
    items.push({
      type: 'warning',
      message: 'No market validation data yet; run interviews or upload research',
      domain: 'market',
    })
  }

  return items.slice(0, 5)
}

export function RecommendationsList() {
  const { journey, getOverallProgress } = useJourneyStore()

  const recommendations = deriveRecommendations(
    getOverallProgress(),
    journey?.currentPhaseId || '',
    journey?.completedActivities || [],
    journey?.phaseProgress || {},
  )

  if (recommendations.length === 0) return null

  return (
    <section aria-label="Recommendations" className="pt-5">
      <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-text-muted mb-3">
        Recommendations
      </div>
      <ul className="space-y-2.5">
        {recommendations.map((rec, idx) => (
          <li key={idx} className="flex items-start gap-3">
            <span
              aria-hidden="true"
              className={cn(
                'w-1.5 h-1.5 rounded-full mt-2 shrink-0',
                PRIORITY_DOT[rec.type],
              )}
            />
            <span className="text-[14px] text-text-secondary flex-1 leading-snug">
              {rec.message}
            </span>
            {rec.domain && (
              <span className="flex items-center gap-1.5 shrink-0 text-[11px] text-text-muted font-mono uppercase tracking-[0.06em]">
                <span
                  aria-hidden="true"
                  className="w-1.5 h-1.5 rounded-full"
                  style={{ backgroundColor: DOMAIN_COLOR[rec.domain] }}
                />
                {DOMAIN_LABEL[rec.domain]}
              </span>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}
